import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Spinner, Modal } from 'react-bootstrap';
import { 
  faToggleOn, 
  faToggleOff, 
  faSignInAlt, 
  faSignOutAlt, 
  faCheckCircle, 
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ATOModeToggle = () => {
  const [modeStatus, setModeStatus] = useState(null);
  const [authStatus, setAuthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authUrl, setAuthUrl] = useState(null);

  useEffect(() => {
    fetchModeStatus();
    fetchAuthStatus();
  }, []);

  const fetchModeStatus = async () => {
    try {
      const response = await fetch('/api/ato/mode');
      const data = await response.json();
      setModeStatus(data);
    } catch (error) {
      console.error('Failed to fetch mode status:', error);
    }
  };

  const fetchAuthStatus = async () => {
    try {
      const response = await fetch('/api/ato/auth-status');
      const data = await response.json();
      setAuthStatus(data);
    } catch (error) {
      console.error('Failed to fetch auth status:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = async (liveMode) => {
    setSwitching(true);
    try {
      const response = await fetch('/api/ato/mode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ liveMode }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setModeStatus(data);
        await fetchAuthStatus();
      } else {
        const error = await response.json();
        alert(`Failed to switch mode: ${error.error}`);
      }
    } catch (error) {
      console.error('Failed to toggle mode:', error);
      alert('Failed to toggle mode');
    } finally {
      setSwitching(false);
    }
  };

  const initiateOAuthLogin = async () => {
    try {
      const response = await fetch('/api/ato/auth-url');
      const data = await response.json();
      setAuthUrl(data.url);
      setShowAuthModal(true);
    } catch (error) {
      console.error('Failed to get auth URL:', error);
      alert('Failed to initiate ATO login');
    }
  };

  const handleOAuthLogin = () => {
    if (authUrl) {
      window.open(authUrl, '_blank', 'width=600,height=700,scrollbars=yes,resizable=yes');
    }
  };

  const refreshStatus = () => {
    fetchModeStatus();
    fetchAuthStatus();
  };

  if (loading) {
    return (
      <Card className="mb-4">
        <Card.Body className="text-center">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading ATO status...
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            ATO Integration Status
          </h5>
        </Card.Header>
        <Card.Body>
          {/* Mode Status */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>Current Mode:</strong>
                <Badge 
                  bg={modeStatus?.liveMode ? "success" : "secondary"} 
                  className="ms-2"
                >
                  {modeStatus?.liveMode ? "LIVE" : "SIMULATED"}
                </Badge>
              </div>
              <Button
                variant={modeStatus?.liveMode ? "outline-danger" : "outline-success"}
                size="sm"
                onClick={() => toggleMode(!modeStatus?.liveMode)}
                disabled={switching}
              >
                {switching ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FontAwesomeIcon 
                      icon={modeStatus?.liveMode ? faToggleOff : faToggleOn} 
                      className="me-1" 
                    />
                    Switch to {modeStatus?.liveMode ? "Simulated" : "Live"}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Authentication Status */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>Authentication:</strong>
                <Badge 
                  bg={authStatus?.authenticated ? "success" : "warning"} 
                  className="ms-2"
                >
                  {authStatus?.authenticated ? "AUTHENTICATED" : "NOT AUTHENTICATED"}
                </Badge>
              </div>
              {!authStatus?.authenticated && modeStatus?.liveMode && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={initiateOAuthLogin}
                >
                  <FontAwesomeIcon icon={faSignInAlt} className="me-1" />
                  Login to ATO
                </Button>
              )}
            </div>
          </div>

          {/* Configuration Status */}
          <div className="mb-3">
            <strong>Configuration:</strong>
            <div className="mt-2">
              <div className="d-flex align-items-center mb-1">
                <FontAwesomeIcon 
                  icon={authStatus?.hasClientId ? faCheckCircle : faExclamationTriangle} 
                  className={`me-2 ${authStatus?.hasClientId ? 'text-success' : 'text-warning'}`}
                />
                <span className="small">
                  Client ID: {authStatus?.hasClientId ? 'Configured' : 'Missing'}
                </span>
              </div>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon 
                  icon={authStatus?.hasClientSecret ? faCheckCircle : faExclamationTriangle} 
                  className={`me-2 ${authStatus?.hasClientSecret ? 'text-success' : 'text-warning'}`}
                />
                <span className="small">
                  Client Secret: {authStatus?.hasClientSecret ? 'Configured' : 'Missing'}
                </span>
              </div>
            </div>
          </div>

          {/* Mode Information */}
          <Alert variant={modeStatus?.liveMode ? "warning" : "info"} className="mb-0">
            <strong>
              {modeStatus?.liveMode ? "Live Mode" : "Simulated Mode"}
            </strong>
            <br />
            {modeStatus?.liveMode ? (
              <>
                <strong>⚠️ LIVE MODE:</strong> Real ATO API integration. Requires valid OAuth tokens and ATO credentials.
                <br />
                <small>
                  • Submissions will be sent to actual ATO API<br />
                  • Requires ATO developer registration<br />
                  • Authentication required
                </small>
              </>
            ) : (
              <>
                <strong>ℹ️ SIMULATED MODE:</strong> Safe for testing and demos. No real ATO API calls.
                <br />
                <small>
                  • Perfect for development and testing<br />
                  • Shows correct ATO API payload structure<br />
                  • No authentication required
                </small>
              </>
            )}
          </Alert>

          <div className="mt-3 text-end">
            <Button variant="outline-secondary" size="sm" onClick={refreshStatus}>
              Refresh Status
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* OAuth Login Modal */}
      <Modal show={showAuthModal} onHide={() => setShowAuthModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faSignInAlt} className="me-2" />
            ATO Authentication
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You're about to authenticate with the ATO API. This will open a new window 
            where you can log in with your ATO credentials.
          </p>
          <Alert variant="warning">
            <strong>Important:</strong> Make sure you have:
            <ul className="mb-0 mt-2">
              <li>Valid ATO developer credentials</li>
              <li>ATO Client ID and Client Secret configured</li>
              <li>Proper redirect URI set up</li>
            </ul>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAuthModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleOAuthLogin}>
            <FontAwesomeIcon icon={faSignInAlt} className="me-1" />
            Open ATO Login
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ATOModeToggle;
